/**
 * "traceable": numbered points; drag from the current point to the next one
 * to draw the line (PlayspaceTraceable.cs / TraceableElementPlayer.cs).
 * Items = points - 1. Releasing elsewhere just cancels the stroke.
 */
import { useRef, useState } from "react";
import type { SlideOf } from "@makeit/schema";
import { layoutStyle } from "../Stage";
import { useActivity } from "../activity";
import { toStagePoint, useDraggable, useDropZone, useStageScale } from "../drag";

interface Pt { x: number; y: number }

export function TraceableSlide({ slide }: { slide: SlideOf<"traceable"> }) {
  const points = slide.points;
  const activity = useActivity(Math.max(1, points.length - 1));
  const [current, setCurrent] = useState(0); // index of the next point to start from
  const [live, setLive] = useState<Pt | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const scale = useStageScale();

  // The point's position is its centre (TraceableElement pivot 0.5, 0.5).
  const center = (i: number): Pt => {
    const l = points[i].layout;
    return { x: l.x, y: -l.y };
  };

  const onDrop = (zone: string | null) => {
    setLive(null);
    if (zone === `p:${current + 1}`) {
      setCurrent((c) => c + 1);
      activity.correct();
    }
    return false;
  };

  return (
    <div className="traceable" ref={rootRef}>
      <svg className="connect-lines" width="2048" height="1536">
        {Array.from({ length: current }, (_, i) => {
          const a = center(i);
          const b = center(i + 1);
          return <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} className="line-ok" />;
        })}
        {live && current < points.length - 1 && <line x1={center(current).x} y1={center(current).y} x2={live.x} y2={live.y} className="line-live" />}
      </svg>
      {points.map((p, i) => (
        <TracePoint
          key={p.id}
          index={i}
          label={i === 0 || slide.showAllNumbers ? String(i + 1) : ""}
          style={layoutStyle({ ...p.layout, x: p.layout.x - (p.layout.width || 120) / 2, y: p.layout.y + (p.layout.height || 120) / 2, width: p.layout.width || 120, height: p.layout.height || 120 })}
          active={i === current && current < points.length - 1}
          done={i < current || (i === current && current === points.length - 1)}
          onDrop={onDrop}
          onMove={(client) => rootRef.current && setLive(toStagePoint(rootRef.current, client.x, client.y, scale))}
        />
      ))}
    </div>
  );
}

function TracePoint({ index, label, style, active, done, onDrop, onMove }: { index: number; label: string; style: React.CSSProperties; active: boolean; done: boolean; onDrop: (z: string | null) => boolean; onMove: (p: Pt) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useDropZone(`p:${index}`, ref, true);
  const drag = useDraggable({ onDrop, onMove, disabled: !active });
  return (
    <div ref={ref} data-zone={`p:${index}`} className={`trace-point ${active ? "active" : ""} ${done ? "done" : ""}`} style={{ ...style, touchAction: "none", cursor: active ? "grab" : undefined }} {...drag.handlers}>
      {label}
    </div>
  );
}
